import { getPodcastsAnalytics } from "@/api/analyticsPodcasts.api"

// ---------------------------------------------------------------------------
// Unified podcasts analytics action
// ---------------------------------------------------------------------------

/**
 * Single entry point for podcast analytics fetching.
 *
 * When comparison is enabled, primary and comparison datasets are fetched in
 * parallel and committed together so the UI renders both simultaneously.
 */
export const fetchPodcastsAnalytics = async (vuex, { accountId, params }) => {
  vuex.commit("reset")
  vuex.commit("setParams", {
    from_date: params.from_date,
    to_date: params.to_date,
    offset: params.offset,
  })

  const wantsComparison =
    params.enableComparison && params.comparisonFromDate && params.comparisonToDate

  if (wantsComparison) {
    // --- Synchronized parallel fetch (primary + comparison) ---
    const comparisonApiParams = {
      from_date: params.comparisonFromDate,
      to_date: params.comparisonToDate,
      offset: params.offset,
    }

    if (params.podcast_keys) {
      comparisonApiParams.podcast_keys = params.podcast_keys
    }

    vuex.commit("setComparisonParams", {
      from_date: params.comparisonFromDate,
      to_date: params.comparisonToDate,
    })

    // Both API calls fire simultaneously
    const [primaryResult, comparisonResult] = await Promise.allSettled([
      getPodcastsAnalytics(accountId, params),
      getPodcastsAnalytics(accountId, comparisonApiParams),
    ])

    // Primary must succeed
    if (primaryResult.status === "fulfilled") {
      vuex.commit("setDatasetStat", { dataset: "primary", value: primaryResult.value })
    } else {
      throw primaryResult.reason
    }

    // Comparison failure is non-fatal
    if (comparisonResult.status === "fulfilled") {
      vuex.commit("setDatasetStat", { dataset: "comparison", value: comparisonResult.value })
      vuex.commit("setComparisonMode", true)
    } else {
      console.warn("Comparison fetch failed, primary data preserved:", comparisonResult.reason)
      vuex.commit("setComparisonMode", false)
    }
  } else {
    // --- Primary-only ---
    vuex.commit("setComparisonMode", false)
    const podcastsAnalytics = await getPodcastsAnalytics(accountId, params)
    vuex.commit("setDatasetStat", { dataset: "primary", value: podcastsAnalytics })
  }
}

// ---------------------------------------------------------------------------
// Simple mutations exposed as actions
// ---------------------------------------------------------------------------

export const selectPodcasts = async (context, payload) => {
  context.commit("setSelectedPodcasts", payload)
}

export const setAnalyticsPeriod = async (context, payload) => {
  context.commit("setParams", payload)
}
